
export default function UXStrategy() {
  const strategies = [
    {
      number: '01',
      title: 'Simplifying Complex Workflows',
      description: 'Construction workflows involve multiple approval stages, conditional logic, and regulatory requirements. The UX strategy focused on progressive disclosure—showing users only what they need at each step while maintaining access to deeper functionality when required.',
      details: 'We designed task-based interfaces that guide users through complex processes without overwhelming them with options. Each workflow was mapped to real-world construction scenarios.',
      icon: 'ri-flow-chart'
    },
    {
      number: '02',
      title: 'Reducing Cognitive Load',
      description: 'With seven modules consolidated into one platform, preventing cognitive overload was critical. We established a consistent navigation pattern, unified terminology across modules, and created a persistent context bar.',
      details: 'Role-based dashboards were designed to surface the most relevant information first, reducing the need for users to navigate deeply into the system for routine tasks.',
      icon: 'ri-mind-map'
    },
    {
      number: '03',
      title: 'Designing for Scalability',
      description: 'Enterprise construction organizations operate across multiple projects, regions, and teams. The UX architecture needed to support both small teams and organizations with thousands of users.',
      details: 'We designed flexible permission systems, reusable template structures, and modular components that could be configured rather than custom-built.',
      icon: 'ri-expand-up-down-line'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <span className="inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ backgroundColor: '#F16051', color: 'white' }}>
              Design Approach
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#182423' }}>
              UX Strategy
            </h2>
            <p className="text-lg text-gray-600">
              A thoughtful approach to solving complex enterprise challenges through user-centered design principles.
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 rounded-3xl" style={{ background: 'linear-gradient(135deg, #F16051 0%, #e54d3d 100%)', transform: 'rotate(3deg)' }}></div>
            <div className="relative bg-white rounded-3xl p-8 shadow-xl">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#182423' }}>
                  <i className="ri-lightbulb-line text-white text-xl"></i>
                </div>
                <span className="font-bold text-gray-900">Core Principle</span>
              </div>
              <p className="text-gray-600 italic">
                "Design for the complexity of enterprise workflows while maintaining the simplicity users expect from modern applications."
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {strategies.map((strategy, index) => (
            <div 
              key={index} 
              className="group relative bg-gradient-to-r from-gray-50 to-white rounded-3xl p-8 border border-gray-100 hover:shadow-lg transition-all duration-300 overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-2 h-full" style={{ backgroundColor: index % 2 === 0 ? '#F16051' : '#182423' }}></div>
              
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-shrink-0">
                  <div 
                    className="w-20 h-20 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: index % 2 === 0 ? '#F16051' : '#182423' }}
                  >
                    <span className="text-3xl font-black text-white">{strategy.number}</span>
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <i className={`${strategy.icon} text-2xl`} style={{ color: index % 2 === 0 ? '#F16051' : '#182423' }}></i>
                    <h3 className="text-2xl font-bold text-gray-900">{strategy.title}</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed mb-4">{strategy.description}</p>
                  <p className="text-gray-500 leading-relaxed">{strategy.details}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
